import {
  Box,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
  Chip,
  List,
  ListItem,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { fetchCustomerRestaurantAsync } from '../../customer/restaurant/customerRestaurantSlice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { TabContext, TabPanel } from '@mui/lab';
import MealItem from '../../../app/data/models/mealItem';
import { useTranslation } from 'react-i18next';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import { formatCurrency } from '../../../app/utils/formatters';
import QuantitySelector from '../../../app/components/QuantitySelector';
import {
  Cart,
  CartItem,
  CartItemCustomization,
} from '../../../app/data/models/cart';
import { useCustomizations } from '../../../hooks/useCustomizations';
import {
  isMenuAvailable,
  formatMenuTime,
  isMenuEnabledForToday,
} from '../../../app/utils/menuAvailability';
import dayjs from 'dayjs';
import { AvailabilityScheduleDto } from '../../../app/data/models/common/availability';
import Menu from '../../../app/data/models/menu';
import MealItemCard from '../../customer/restaurant/MealItemCard';
import MealItemCustomizationSelector from '../../customer/restaurant/MealItemCustomizationSelector';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import EmptyScreen from '../../../app/components/EmptyScreen';
import {
  loadRestaurantCartsAsync,
  updateProductQuantity,
  updateCartDetails,
} from './waiterOrderSlice';
import { v4 as uuidv4 } from 'uuid';
import { OrderType } from '../../../app/data/models/orders/orderType';
import OrderTypeSelector from '../../customer/cart/OrderTypeSelector';
import { userFullName } from '../../../app/data/models/user';

const RESTAURANT_TABLE_KEY = (restaurantId: string) =>
  `restaurant_${restaurantId}_table_number`;

const WaiterCreateOrderPage = () => {
  const { t } = useTranslation();
  const { restaurantId, cartId: cartIdParam } = useParams<{
    restaurantId: string;
    cartId: string;
  }>();
  const [cartId, setCartId] = useState(cartIdParam ?? uuidv4());
  const [searchParams] = useSearchParams();
  const { restaurant, status } = useAppSelector(
    (state) => state.customerRestaurant
  );
  const dispatch = useAppDispatch();
  const { carts } = useAppSelector((state) => state.waiterOrder);
  const { user } = useAppSelector((state) => state.account);
  const cart = carts.find((c: Cart) => c.id === cartId);
  const [selectedMenu, setSelectedMenu] = useState(`${0}`);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [availableMenus, setAvailableMenus] = useState<string[]>([]);
  const navigate = useNavigate();

  // State for order type and customer details
  const [orderType, setOrderType] = useState<OrderType>(
    cart?.orderType || OrderType.DineIn
  );
  const [tableNumber, setTableNumber] = useState<number | undefined>(
    cart?.tableNumber
  );
  const [customerName, setCustomerName] = useState<string>(
    cart?.customerName || ''
  );
  const [customerPhone, setCustomerPhone] = useState<string>(
    cart?.customerPhone || ''
  );

  // Error states
  const [tableNumberError, setTableNumberError] = useState<string | null>(null);
  const [customerNameError, setCustomerNameError] = useState<string | null>(
    null
  );
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

  // Handle order id from URL on component mount
  useEffect(() => {
    const tableParam = searchParams.get('');
    if (tableParam) {
      const tableNumber = Number(tableParam);
      if (!isNaN(tableNumber)) {
        // Store table number with restaurant context
        sessionStorage.setItem(
          RESTAURANT_TABLE_KEY(restaurantId),
          tableNumber.toString()
        );
      }
    }
  }, [restaurantId, searchParams]);

  useEffect(() => {
    setCartId(cartIdParam ?? uuidv4());
  }, [cartIdParam]);

  const onQuantityChange = (
    cartItemId: string,
    mealItem: MealItem,
    quantity: number,
    customizations?: CartItemCustomization[]
  ) => {
    const cartItem = {
      cartItemId,
      mealItemId: mealItem.id,
      pictureUrl: mealItem.pictureUrl,
      name: mealItem.name,
      price: mealItem.price,
      customizations,
    };
    dispatch(
      updateProductQuantity({
        cartItem,
        quantity,
        restaurantId,
        cartId,
        waiterId: user?.id,
        waiterName: userFullName(user),
      })
    );
  };

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setSelectedMenu(`${newValue}`);
  };

  const header = (
    <ListTitleBox
      title={t('orders.create.title')}
      onBackClick={() =>
        navigate(`/restaurant-waiter/restaurants/${restaurantId}/orders`)
      }
    />
  );

  useEffect(() => {
    dispatch(fetchCustomerRestaurantAsync(restaurantId));
    dispatch(loadRestaurantCartsAsync(restaurantId));
  }, [restaurantId, dispatch]);

  useEffect(() => {
    const checkAvailability = () => {
      const available =
        restaurant?.menus
          ?.filter((menu) => isMenuAvailable(menu.availabilitySchedules))
          .map((menu) => menu.id) ?? [];
      setAvailableMenus(available);
    };

    checkAvailability(); // Initial check
    const interval = setInterval(checkAvailability, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [restaurant]);

  // Handle table number change
  const handleTableNumberChange = (value: number | undefined) => {
    setTableNumber(value);
    setTableNumberError(null);

    if (restaurantId && cartId) {
      dispatch(
        updateCartDetails({
          cartId,
          restaurantId: restaurantId,
          updates: {
            tableNumber: value,
            waiterId: user?.id,
            waiterName: userFullName(user),
          },
        })
      );
    }
  };

  // Handle order type change
  const handleOrderTypeChange = (type: OrderType) => {
    setOrderType(type);
    // Clear errors when switching order types
    setTableNumberError(null);
    setCustomerNameError(null);
    setPhoneNumberError(null);

    if (restaurantId && cartId) {
      dispatch(
        updateCartDetails({
          cartId,
          restaurantId: restaurantId,
          updates: {
            orderType: type,
            waiterId: user?.id,
            waiterName: userFullName(user),
          },
        })
      );
    }
  };

  // Handle customer name change
  const handleCustomerNameChange = (name: string) => {
    setCustomerName(name);
    setCustomerNameError(null);

    if (restaurantId && cartId) {
      dispatch(
        updateCartDetails({
          cartId,
          restaurantId: restaurantId,
          updates: {
            customerName: name,
            waiterId: user?.id,
            waiterName: userFullName(user),
          },
        })
      );
    }
  };

  // Handle phone number change
  const handleCustomerPhoneChange = (phone: string) => {
    setCustomerPhone(phone);
    setPhoneNumberError(null);

    if (restaurantId && cartId) {
      dispatch(
        updateCartDetails({
          cartId,
          restaurantId: restaurantId,
          updates: {
            customerPhone: phone,
            waiterId: user?.id,
            waiterName: userFullName(user),
          },
        })
      );
    }
  };

  // Load cart details on mount
  useEffect(() => {
    if (cart) {
      setOrderType(cart.orderType || OrderType.DineIn);
      setTableNumber(cart.tableNumber);
      setCustomerName(cart.customerName || '');
      setCustomerPhone(cart.customerPhone || '');
    }
  }, [cart]);

  const ListViewMealItem = ({
    mealItem,
    menuSchedule,
    isAvailable,
  }: {
    mealItem: MealItem;
    menuSchedule: AvailabilityScheduleDto | undefined;
    isAvailable: boolean;
  }) => {
    const quantity = cart?.orderItems
      .filter((item: CartItem) => item.mealItemId === mealItem.id)
      .reduce((sum: number, item: CartItem) => sum + item.quantity, 0);

    const {
      showCustomizations,
      setShowCustomizations,
      handleQuantityChange,
      handleAddCustomizations,
    } = useCustomizations(mealItem, (cartItemId, qty, customizations) =>
      onQuantityChange(cartItemId, mealItem, qty, customizations)
    );

    return (
      <>
        <ListItem
          divider
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            opacity: isAvailable ? 1 : 0.7,
            position: 'relative',
            '&:hover': isAvailable ? { bgcolor: 'action.hover' } : undefined,
          }}
        >
          {/* Content */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                mb: 0,
                lineHeight: 1.2,
              }}
            >
              {mealItem.name}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {formatCurrency(mealItem.price)}
              </Typography>
              {mealItem.rating && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  • {mealItem.rating}% ({mealItem.reviewCount})
                </Typography>
              )}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: '0.8rem',
                lineHeight: 1.4,
              }}
            >
              {mealItem.description}
            </Typography>
          </Box>

          {/* Image and Quantity Selector */}
          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={mealItem.pictureUrl || '/meal-placeholder.png'}
              alt={mealItem.name}
              sx={{
                width: 120,
                height: 120,
                borderRadius: 1,
                objectFit: 'cover',
                filter: !isAvailable ? 'grayscale(100%)' : 'none',
              }}
            />

            {!isAvailable && menuSchedule && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  p: 1,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('restaurant.item-unavailable')}
                </Typography>
              </Box>
            )}

            {isAvailable && (
              <Box sx={{ position: 'absolute', right: 4, bottom: 12 }}>
                <QuantitySelector
                  quantity={quantity ?? 0}
                  onQuantityChange={handleQuantityChange}
                  hasCustomizations={!!mealItem.customizations?.length}
                />
              </Box>
            )}
          </Box>
        </ListItem>

        <MealItemCustomizationSelector
          open={showCustomizations}
          onClose={() => setShowCustomizations(false)}
          customizations={mealItem.customizations || []}
          onAddToCart={handleAddCustomizations}
          itemName={mealItem.name}
          itemPrice={mealItem.price}
          mealItemId={mealItem.id}
        />
      </>
    );
  };

  const filteredMenus = restaurant?.menus?.filter((menu) => {
    return isMenuEnabledForToday(menu.availabilitySchedules);
  });

  const renderMealItems = (mealItems: MealItem[], menu: Menu) => {
    if (isMobile) {
      return (
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {mealItems.map((mealItem) => (
            <ListViewMealItem
              key={mealItem.id}
              mealItem={mealItem}
              menuSchedule={menu.availabilitySchedules?.find(
                (s) => s.dayOfWeek === dayjs().day()
              )}
              isAvailable={availableMenus.includes(menu.id)}
            />
          ))}
        </List>
      );
    }

    return (
      <Grid container spacing={3}>
        {mealItems.map((mealItem) => (
          <Grid key={mealItem.id} item xs={12} sm={6} md={4}>
            <MealItemCard
              quantity={
                cart?.orderItems
                  .filter((item) => item.mealItemId === mealItem.id)
                  .reduce(
                    (sum: number, item: CartItem) => sum + item.quantity,
                    0
                  ) ?? 0
              }
              mealItem={mealItem}
              onQuantityChange={(cartItemId, quantity, customizations) =>
                onQuantityChange(cartItemId, mealItem, quantity, customizations)
              }
              isAvailable={availableMenus.includes(menu.id)}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const handleViewCart = () => {
    navigate(
      `/restaurant-waiter/restaurants/${restaurantId}/orders/cart/${cartId}`
    );
  };

  if (status.includes('pending')) return <LoadingComponent header={header} />;

  if (!restaurant)
    return (
      <EmptyScreen
        title={t('orders.create.title')}
        emptyText={t('restaurant.not-found')}
      />
    );

  return (
    <>
      {header}
      {/* Order Type Selector */}
      <OrderTypeSelector
        orderType={orderType}
        tableNumber={tableNumber}
        tableNumberError={tableNumberError}
        customerName={customerName}
        customerPhone={customerPhone}
        onOrderTypeChange={handleOrderTypeChange}
        onTableNumberChange={handleTableNumberChange}
        onCustomerNameChange={handleCustomerNameChange}
        onCustomerPhoneChange={handleCustomerPhoneChange}
        phoneNumberError={phoneNumberError}
        customerNameError={customerNameError}
      />
      {/* Menu Tabs */}
      <Box sx={{ paddingBottom: '72px' }}>
        <Paper elevation={3} sx={{ p: 0.25 }}>
          <TabContext value={selectedMenu}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={selectedMenu}
                onChange={handleChange}
                aria-label={t('restaurant.menu')}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: 'auto',
                    px: isMobile ? 1 : 3,
                    py: 1,
                  },
                }}
              >
                {filteredMenus?.map((menu, index) => {
                  return (
                    <Tab
                      key={menu.id}
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'top',
                            gap: 0.5,
                            height: '100%',
                          }}
                        >
                          <Typography variant="subtitle1">
                            {menu.title}
                          </Typography>
                          <Chip
                            label={t('restaurant.menu-items-count', {
                              count: menu.mealItems.length,
                            })}
                            size="small"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', textAlign: 'center' }}
                          >
                            {formatMenuTime(menu.availabilitySchedules)}
                          </Typography>
                        </Box>
                      }
                      value={`${index}`}
                    />
                  );
                })}
              </Tabs>
            </Box>

            {filteredMenus?.map((menu, index) => (
              <TabPanel
                sx={{ p: isMobile ? 1 : 2 }}
                key={menu.id}
                value={`${index}`}
              >
                {renderMealItems(menu.mealItems, menu)}
              </TabPanel>
            ))}
          </TabContext>
        </Paper>
      </Box>
      {/* Centered Button for View Cart */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          aria-label="view cart"
          sx={{
            padding: '8px 16px',
            minHeight: '40px',
            minWidth: '200px',
          }}
          onClick={handleViewCart}
        >
          <Typography variant="button">{t('cart.view-cart')}</Typography>
        </Button>
      </Box>
    </>
  );
};

export default WaiterCreateOrderPage;
